/**
 * When to show "Back to reports" above a report (Steven, 2026-11-04).
 *
 * Reports open from cards on the Reports page, and the sidebar has only the
 * one Reports link, so without this the way back is the sidebar. Two cases:
 *
 * - A report that lives only under Reports (Loyalty, OMNI Performance, ...)
 *   always shows the link, however the user arrived (card, pin, bookmark).
 * - A shortcut card to a page another section owns (Commerce > Inventory)
 *   shows it only when the user came from the Reports page, since someone who
 *   opened it from the Commerce menu never saw Reports.
 *
 * The link shows on the report's own address only, never on a sub-page that
 * carries its own back link (an inventory count's detail page, for example).
 */

import { COCKPIT_DEPARTMENTS } from '@/data/tools.config';
import { toolHrefFor } from './toolRouting';
import { REPORTS_DEPT_ID } from './reportsPage';

/** Navigation state the Reports page cards attach, read by the shell. */
export const FROM_REPORTS_STATE = { fromReports: true } as const;

export function cameFromReports(state: unknown): boolean {
  return !!state && typeof state === 'object' && (state as { fromReports?: unknown }).fromReports === true;
}

function reportHrefs(): { own: Set<string>; shortcut: Set<string> } {
  const elsewhere = new Set<string>();
  for (const dept of COCKPIT_DEPARTMENTS) {
    if (dept.id === REPORTS_DEPT_ID) continue;
    for (const tool of dept.tools) elsewhere.add(toolHrefFor(dept.id, tool.id));
  }
  const own = new Set<string>();
  const shortcut = new Set<string>();
  const reports = COCKPIT_DEPARTMENTS.find((d) => d.id === REPORTS_DEPT_ID);
  for (const tool of reports?.tools ?? []) {
    const href = toolHrefFor(REPORTS_DEPT_ID, tool.id);
    (elsewhere.has(href) ? shortcut : own).add(href);
  }
  return { own, shortcut };
}

const HREFS = reportHrefs();

export function showReportsBackLink(pathname: string, fromReports: boolean): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (HREFS.own.has(path)) return true;
  return fromReports && HREFS.shortcut.has(path);
}
